import React from 'react';
import type { AnalyticsBoxCoordinates } from '../../types/analytics';

interface Offset {
    x: number;
    y: number;
}

/**
 * Calculate safe offset that keeps path within boundaries (0-100%)
 */
const calculateSafeOffset = (
    coords: AnalyticsBoxCoordinates[],
    desiredOffset?: Offset
): Offset => {
    if (!desiredOffset) {
        return { x: 0, y: 0 };
    }
    
    // Calculate what the points would be with the offset
    const points = coords.map(([x1, y1, x2, y2]) => ({
        x: (x1 + x2) / 2 + desiredOffset.x,
        y: (y1 + y2) / 2 + desiredOffset.y
    }));
    
    // Check if any point exceeds boundaries
    const exceedsBounds = points.some(p => 
        p.x < 0 || p.x > 100 || p.y < 0 || p.y > 100
    );
    
    // If it exceeds bounds, don't apply offset
    return exceedsBounds ? { x: 0, y: 0 } : desiredOffset;
};

interface TrajectoryOverlayProps {
    /** Array of smart motion boxes or normalised alert-history movement points */
    allCoords: AnalyticsBoxCoordinates[];
    /** Color for the trajectory path */
    colour: string;
    /** Unique identifier for this trajectory */
    id: string;
    /** Offset to apply to the path { x, y } */
    offset?: Offset;
    /** Disable SVG filters for performance testing on iOS devices */
    disableFilters?: boolean;
}

/**
 * Renders a smooth trajectory path with start dot and arrow for analytics tracks.
 */
const TrajectoryOverlay: React.FC<TrajectoryOverlayProps> = ({
    allCoords,
    colour,
    id,
    offset = { x: 0, y: 0 },
    disableFilters = false
}) => {
    const safeOffset = calculateSafeOffset(allCoords, offset);
    
    // Calculate if trajectory is significant enough to display
    const isTrajectorySignificant = (coords: AnalyticsBoxCoordinates[]): boolean => {
        const MIN_PATH_LENGTH = 10; // Minimum total path length in pixels

        const points = coords.map(([x1, y1, x2, y2]) => ({
            x: (x1 + x2) / 2,
            y: y2 - 5
        }));
            
        // Calculate total path length
        let totalLength = 0;
        for (let i = 1; i < points.length; i++) {
            const dx = points[i].x - points[i-1].x;
            const dy = points[i].y - points[i-1].y;
            totalLength += Math.sqrt(dx * dx + dy * dy);
        }
            
        // Skip if path is too short
        if (totalLength < MIN_PATH_LENGTH) {
            return false;
        }
        return true;
    };
    
    // Create smooth curved path using Catmull-Rom splines
    const createSmoothPath = (coords: AnalyticsBoxCoordinates[]): string => {
        if (!coords || coords.length === 0) {
            return '';
        }
        
        // Calculate center points between each coordinate pair with offset
        const points = coords.map(([x1, y1, x2, y2]) => ({
            x: (x1 + x2) / 2 + safeOffset.x,
            y: (y1 + y2) / 2 + safeOffset.y
        }));
        
        if (points.length < 2) {
            return '';
        }
        
        let path = `M ${points[0].x} ${points[0].y}`;
        
        if (points.length === 2) {
            path += ` L ${points[1].x} ${points[1].y}`;
            return path;
        }
        
        // Tension parameter for smoothness (0 = straight, 0.5 = moderate, 1 = very smooth)
        const tension = 0.5;
        
        // Create smooth Catmull-Rom curves
        for (let i = 0; i < points.length - 1; i++) {
            const p0 = i > 0 ? points[i - 1] : points[i];
            const p1 = points[i];
            const p2 = points[i + 1];
            const p3 = i < points.length - 2 ? points[i + 2] : points[i + 1];
            
            // Calculate control points using Catmull-Rom to Bezier conversion
            const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
            const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
            const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
            const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;
            
            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }
        
        return path;
    };
    
    const isSignificant = isTrajectorySignificant(allCoords);
    
    // Don't render if trajectory is not significant
    if (!isSignificant) {
        return null;
    }
    
    const pathData = createSmoothPath(allCoords);
    const glowId = `glow-${id}`;
    const startDotId = `startDot-${id}`;
    const arrowheadId = `arrowhead-${id}`;
    
    return (
        <svg
            className="trajectory-overlay"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
            }}
        >
            <defs>
                {!disableFilters && (
                    <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%" filterUnits="userSpaceOnUse">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur1" />
                        <feGaussianBlur in="SourceGraphic" stdDeviation="3.0" result="blur2" />
                        <feFlood floodColor="white" floodOpacity="0.8" result="color1" />
                        <feFlood floodColor="white" floodOpacity="0.4" result="color2" />
                        <feComposite in="color1" in2="blur1" operator="in" result={`${glowId}1`} />
                        <feComposite in="color2" in2="blur2" operator="in" result={`${glowId}2`} />
                        <feMerge>
                            <feMergeNode in={`${glowId}2`} />
                            <feMergeNode in={`${glowId}1`} />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                )}
                <marker id={startDotId} markerWidth="10" markerHeight="10" refX="5" refY="5" markerUnits="strokeWidth">
                    <circle cx="5" cy="5" r="2" fill={colour} filter={!disableFilters ? `url(#${glowId})` : undefined} />
                </marker>
                <marker id={arrowheadId} markerWidth="10" markerHeight="10" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                    <polygon points="0 0, 5 3, 0 6" fill={colour} filter={!disableFilters ? `url(#${glowId})` : undefined} />
                </marker>
            </defs>
            <path
                d={pathData}
                className="trajectory-path"
                stroke={colour}
                strokeWidth="0.5"
                fill="none"
                markerStart={`url(#${startDotId})`}
                markerEnd={`url(#${arrowheadId})`}
                filter={!disableFilters ? `url(#${glowId})` : undefined}
            />
        </svg>
    );
};

export default TrajectoryOverlay;
